import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import {
  asPlaybookId,
  getOutcomeDefinitionFromPlaybook,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import type {
  CustomerExperienceScoreAggregate,
  TopicDrilldownStats,
  TopicId,
  TopicMatch,
} from "@repo/app-types/entities/topic";
import { Size } from "@repo/ui/helpers/helpers";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { SlideoverContainer } from "@repo/ui/Slideover/slideoverContainer";
import {
  SlideoverDialog,
  SlideoverDialogHeader,
} from "@repo/ui/Slideover/slideoverDialog";
import { useState, type FC } from "react";
import { useLocation, useNavigate } from "react-router";
import { usePlaybookByIdQuery } from "../../../api/entities/playbook.js";
import type { TopicStatsQueryArg } from "../../../api/entities/topic.js";
import {
  useTopicDrilldownQuery,
  useTopicMatchesQuery,
} from "../../../api/entities/topic.js";
import { appPaths } from "../../../app/appPaths.js";
import { TopicCustomerExperienceScoreBreakdown } from "./topicCustomerExperienceScoreBreakdown.js";
import { TopicMatchesList } from "./topicMatchesList.js";
import { TopicMatchSlideoverDialog } from "./topicMatchSlideoverDialog.js";
import { TopicSummaryStrip } from "./topicSummaryStrip.js";
import { TopicTrendChart } from "./topicTrendChart.js";

interface TopicSlideoverContainerProps {
  topicId: TopicId | null;
  playbookId: PlaybookId;
  queryArg: TopicStatsQueryArg;
  winRateImpact: number | null;
  customerExperienceScoreImpact: number | null;
  customerExperienceBaseline: CustomerExperienceScoreAggregate | null;
}

export const TopicSlideoverContainer: FC<TopicSlideoverContainerProps> = ({
  topicId,
  playbookId,
  queryArg,
  winRateImpact,
  customerExperienceScoreImpact,
  customerExperienceBaseline,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = appPaths.playbookTopics.build({ playbookId });
  const isOpen = topicId !== null;
  const [selectedMatch, setSelectedMatch] = useState<{
    match: TopicMatch;
    topicName: string;
  } | null>(null);

  return (
    <>
      <SlideoverContainer
        isOpen={isOpen}
        isDismissable
        size={Size.MEDIUM}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedMatch(null);
            // Preserve the current query string (e.g. `?expand=...`,
            // filter params) so closing the slideover doesn't collapse
            // the expanded subtopic list or reset filters.
            navigate(`${basePath}${location.search}`);
          }
        }}
      >
        {topicId ? (
          <TopicSlideoverContent
            topicId={topicId}
            playbookId={playbookId}
            queryArg={queryArg}
            winRateImpact={winRateImpact}
            customerExperienceScoreImpact={customerExperienceScoreImpact}
            customerExperienceBaseline={customerExperienceBaseline}
            onMatchSelect={(match, topicName) =>
              setSelectedMatch({ match, topicName })
            }
          />
        ) : null}
      </SlideoverContainer>

      <SlideoverContainer
        isOpen={selectedMatch !== null}
        isDismissable
        size={Size.LARGE}
        onOpenChange={(open) => {
          if (!open) setSelectedMatch(null);
        }}
      >
        {selectedMatch ? (
          <TopicMatchSlideoverDialog
            match={selectedMatch.match}
            topicName={selectedMatch.topicName}
          />
        ) : null}
      </SlideoverContainer>
    </>
  );
};

const TopicSlideoverContent: FC<{
  topicId: TopicId;
  playbookId: PlaybookId;
  queryArg: TopicStatsQueryArg;
  winRateImpact: number | null;
  customerExperienceScoreImpact: number | null;
  customerExperienceBaseline: CustomerExperienceScoreAggregate | null;
  onMatchSelect?: (match: TopicMatch, topicName: string) => void;
}> = ({
  topicId,
  playbookId,
  queryArg,
  winRateImpact,
  customerExperienceScoreImpact,
  customerExperienceBaseline,
  onMatchSelect,
}) => {
  const { data: playbook } = usePlaybookByIdQuery(asPlaybookId(playbookId));
  const { data, isLoading } = useTopicDrilldownQuery({
    ...queryArg,
    topicId,
  });
  const matchesQuery = useTopicMatchesQuery({
    ...queryArg,
    topicId,
  });

  const outcomeDef = playbook
    ? getOutcomeDefinitionFromPlaybook(playbook)
    : undefined;

  const topicName = data?.name ?? "";

  return (
    <SlideoverDialog label={data?.name ?? "Topic"}>
      <SlideoverDialogHeader isLoading={isLoading && !data}>
        <h2 className="truncate text-lg font-normal text-foreground">
          {topicName}
        </h2>
      </SlideoverDialogHeader>

      {isLoading && !data ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader style={LoaderStyle.SPINNER} />
        </div>
      ) : data ? (
        <div className="flex-1 overflow-y-auto px-6 pt-3 pb-6">
          <TopicSlideoverSections
            current={data}
            outcomeDef={outcomeDef}
            winRateImpact={winRateImpact}
            customerExperienceScoreImpact={customerExperienceScoreImpact}
            customerExperienceBaseline={customerExperienceBaseline}
            matches={matchesQuery.data?.matches}
            matchesIsLoading={matchesQuery.isLoading}
            onMatchSelect={
              onMatchSelect
                ? (match) => onMatchSelect(match, topicName)
                : undefined
            }
          />
        </div>
      ) : (
        <div className="p-6 text-sm text-muted-foreground">
          Couldn&apos;t load this topic. It may have been deleted or isn&apos;t
          a leaf topic.
        </div>
      )}
    </SlideoverDialog>
  );
};

const CARD_CLASS_NAME =
  "flex flex-1 flex-col rounded-lg border border-border-light bg-white p-4";

const TopicSlideoverSections: FC<{
  current: TopicDrilldownStats;
  outcomeDef: OutcomeDefinition | undefined;
  winRateImpact: number | null;
  customerExperienceScoreImpact: number | null;
  customerExperienceBaseline: CustomerExperienceScoreAggregate | null;
  matches: readonly TopicMatch[] | undefined;
  matchesIsLoading: boolean;
  onMatchSelect?: (match: TopicMatch) => void;
}> = ({
  current,
  outcomeDef,
  winRateImpact,
  customerExperienceScoreImpact,
  customerExperienceBaseline,
  matches,
  matchesIsLoading,
  onMatchSelect,
}) => {
  return (
    <div className="space-y-8">
      {current.description ? (
        <section className="text-sm leading-relaxed text-foreground">
          {current.description}
        </section>
      ) : null}

      <section>
        <SectionHeading>Overview</SectionHeading>
        <TopicSummaryStrip
          current={current}
          winRateImpact={winRateImpact}
          customerExperienceScoreImpact={customerExperienceScoreImpact}
        />
      </section>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <section className="flex flex-col">
          <SectionHeading>CX score breakdown</SectionHeading>
          <TopicCustomerExperienceScoreBreakdown
            distribution={current.customerExperienceDistribution}
            baseline={customerExperienceBaseline}
          />
        </section>
        <section className="flex flex-col">
          <SectionHeading>Matches over time</SectionHeading>
          <div className={CARD_CLASS_NAME}>
            <TopicTrendChart buckets={current.buckets} />
          </div>
        </section>
      </div>

      <section>
        <SectionHeading>Recent matches</SectionHeading>
        {matchesIsLoading && !matches ? (
          <div className="flex items-center justify-center rounded-lg bg-gray-50 p-6">
            <Loader style={LoaderStyle.SPINNER} />
          </div>
        ) : (
          <TopicMatchesList
            matches={matches ?? []}
            outcomeDef={outcomeDef}
            onSelectMatch={(m) => onMatchSelect?.(m)}
          />
        )}
      </section>
    </div>
  );
};

/**
 * Shared section label — matches the style already used by
 * `TopicSummaryStrip` ("OVERVIEW") so every section in the slideover
 * reads as a consistent sibling.
 */
const SectionHeading: FC<{ children: string }> = ({ children }) => (
  <h3 className="mb-2 text-xs font-semibold text-muted-foreground">
    {children}
  </h3>
);
